import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["objectTypeId", "topLevelMenuOptions", "flow", "onOptionClick", "options", "open"];
import { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import fuzzy from 'fuzzy';
import I18n from 'I18n';
import UIMultiLevelDropdown from 'UIComponents/dropdown/UIMultiLevelDropdown';
import UITypeaheadSearch from 'UIComponents/typeahead/search/UITypeaheadSearch';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import makeMenuItem from 'automation-ui-canvas/components/flow-header/menu-bar/utils/makeMenuItem';
import { useGetHasScope, useGetIsUngated } from 'automation-ui-core/hooks/authHooks';
import { getIsClassicWorkflow } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { useHelpSearchOption } from 'automation-ui-canvas/components/flow-header/menu-bar/menuOptions/HelpSearchOption';
import { formatShortcut } from 'automation-ui-canvas/components/flow-header/menu-bar/utils/formatShortcut';
import { jsx as _jsx } from "react/jsx-runtime";
const MAX_RESULTS = 8;
export default function EditorHelpMenuDropdown(_ref) {
  let {
      objectTypeId,
      topLevelMenuOptions,
      flow,
      onOptionClick,
      options,
      open
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [searchText, setSearchText] = useState('');
  const history = useHistory();
  const getIsUngated = useGetIsUngated();
  const getHasScope = useGetHasScope();
  const isClassicWorkflow = Boolean(getIsClassicWorkflow(flow));

  // flatten the list of actionable menu items and build a reverse adjacency map
  const [searchableOptions, parentOptionById] = useMemo(() => {
    const parentOptionByIdMap = new Map();
    const allOptions = [];
    const parentOptionsToScan = [...topLevelMenuOptions];
    while (parentOptionsToScan.length > 0) {
      const parentOption = parentOptionsToScan.shift();
      if (parentOption.options) {
        parentOption.options.flat().forEach(option => {
          parentOptionByIdMap.set(option.id, parentOption);
          if (option.options) {
            parentOptionsToScan.push(option);
          } else if (!option.getIsVisible || option !== null && option !== void 0 && option.getIsVisible({
            getIsUngated,
            getHasScope,
            isClassicWorkflow,
            flow
          })) {
            allOptions.push(option);
          }
        });
      } else {
        allOptions.push(parentOption);
      }
    }
    return [allOptions, parentOptionByIdMap];
  }, [topLevelMenuOptions, getIsUngated, getHasScope, isClassicWorkflow, flow]);
  const breadcrumbMap = useMemo(() => {
    const map = new Map();
    searchableOptions.forEach(option => {
      const breadcrumbs = [];
      let parentOption = option;
      do {
        breadcrumbs.unshift(parentOption);
        parentOption = parentOptionById.get(parentOption.id);
      } while (parentOption);
      map.set(option.id, breadcrumbs);
    });
    return map;
  }, [searchableOptions, parentOptionById]);
  const searchOptions = useMemo(() => {
    if (!searchText) {
      return [];
    }
    const filteredOptions = searchableOptions.map(option => {
      var _fuzzy$match;
      return {
        option,
        score: (_fuzzy$match = fuzzy.match(searchText, option.getLabel({
          objectTypeId
        }))) === null || _fuzzy$match === void 0 ? void 0 : _fuzzy$match.score
      };
    }).filter(({
      score
    }) => score !== undefined).sort(({
      score: scoreA
    }, {
      score: scoreB
    }) => scoreB - scoreA).slice(0, MAX_RESULTS).map(({
      option
    }) => {
      var _breadcrumbMap$get;
      const breadcrumbs = (_breadcrumbMap$get = breadcrumbMap.get(option.id)) !== null && _breadcrumbMap$get !== void 0 ? _breadcrumbMap$get : [];
      return makeMenuItem({
        option,
        onOptionClick,
        flow,
        history,
        objectTypeId,
        help: breadcrumbs.length > 1 && /*#__PURE__*/_jsx(UIBreadcrumbs, {
          flush: true,
          className: "m-all-0",
          children: breadcrumbs.map(({
            getLabel
          }) => getLabel({
            objectTypeId
          }))
        })
      });
    });
    if (filteredOptions.length === 0) {
      filteredOptions.push({
        label: I18n.text('automation.editorMenuBar.noSearchResults'),
        // @ts-expect-error the disabled field is respected but not declared
        disabled: true
      });
    }
    return filteredOptions;
  }, [searchableOptions, objectTypeId, history, flow, searchText, onOptionClick, breadcrumbMap]);

  // clear the search bar when the menu closes
  useEffect(() => {
    if (!open) {
      setSearchText('');
    }
  }, [open, setSearchText]);
  const HelpSearchOption = useHelpSearchOption();
  const typeahead = /*#__PURE__*/_jsx(UITypeaheadSearch, {
    className: "m-bottom-2",
    value: searchText,
    onChange: ({
      target: {
        value
      }
    }) => setSearchText(value),
    placeholder: I18n.text('automation.editorMenuBar.searchPlaceholder', {
      keyboardShortcut: formatShortcut(Array.isArray(HelpSearchOption.shortcut) ? HelpSearchOption.shortcut[0] : HelpSearchOption.shortcut).map(({
        value
      }) => value).join('')
    })
  });
  return /*#__PURE__*/_jsx(UIMultiLevelDropdown, Object.assign({
    options: [typeahead, ...(searchText ? searchOptions : options)],
    dropdownClassName: "p-top-0",
    open: open
  }, rest), searchText);
}